import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { chatbotAPI, handleAPIError } from '../services/api';
import { getPlanIdToNameMap, isStarterPlan, isProfessionalPlan, isEnterprisePlan } from '../config/stripe';
import { 
  X,
  Copy,
  CheckCircle,
  Code,
  Globe,
  Monitor,
  MessageSquare,
  ExternalLink,
  Sparkles,
  Rocket,
  MessageCircleMore,
  Phone,
  Instagram,
  Mail
} from 'lucide-react';

const DeploymentModal = ({ chatbot, isOpen, onClose, onUpdate }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [planDetection, setPlanDetection] = useState({
    isStarterPlan: false,
    isProPlan: false,
    isEnterprisePlan: false,
    planName: '',
    loading: true
  });

  // Plan detection effect
  useEffect(() => {
    const detectPlanType = async () => {
      if (!chatbot?.selected_plan_id) {
        setPlanDetection({
          isStarterPlan: false,
          isProPlan: false,
          isEnterprisePlan: false,
          planName: 'No plan selected',
          loading: false
        });
        return;
      }

      try {
        const selectedPlanId = chatbot.selected_plan_id;
        const planIdToNameMap = await getPlanIdToNameMap();
        const planName = planIdToNameMap[selectedPlanId] || selectedPlanId;

        const [isStarter, isProfessional, isEnterprise] = await Promise.all([
          isStarterPlan(selectedPlanId),
          isProfessionalPlan(selectedPlanId),
          isEnterprisePlan(selectedPlanId)
        ]);

        setPlanDetection({
          isStarterPlan: isStarter,
          isProPlan: isProfessional,
          isEnterprisePlan: isEnterprise,
          planName,
          loading: false
        });
      } catch (error) {
        console.error('Error detecting plan type:', error);
        setPlanDetection({
          isStarterPlan: false,
          isProPlan: false,
          isEnterprisePlan: false,
          planName: 'Plan detection error',
          loading: false
        });
      }
    };

    if (isOpen && chatbot) {
      detectPlanType();
    }
  }, [isOpen, chatbot?.selected_plan_id]);

  const copyToClipboard = async (code, type) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(type);
      toast({
        title: "Copied!",
        description: `${type} code copied to clipboard`,
      });
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy code to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleMarkComplete = async () => {
    setLoading(true);
    try {
      await chatbotAPI.updateChatbotStatus(chatbot.id, 'done');
      toast({
        title: "Chatbot Deployed!",
        description: "Your chatbot has been marked as completed and deployed",
      });
      onUpdate();
      onClose();
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast({
        title: "Failed to Mark Complete",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !chatbot) return null;

  // Extract plan detection values from state
  const { isStarterPlan: isStarterPlanSelected, isProPlan, isEnterprisePlan: isEnterprisePlanSelected, planName } = planDetection;
  
  // CRITICAL FIX: Different messaging based on chatbot status
  const isDoneStatus = chatbot.status === 'done';
  const isDeployStatus = chatbot.status === 'deploy';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-gray-900 border-violet-500/20">
        <CardHeader className="border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-white flex items-center">
                <Rocket className="w-8 h-8 mr-3 text-green-400" />
                Deploy Your Chatbot
              </CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                Your chatbot is ready! Choose how to display it on your website.
              </CardDescription>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Status-specific Header Message */}
          <div className="mb-8">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isDoneStatus ? 'bg-blue-600' : 'bg-green-600'
                  }`}>
                    {isDoneStatus ? (
                      <Code className="w-8 h-8 text-white" />
                    ) : (
                      <CheckCircle className="w-8 h-8 text-white" />
                    )}
                  </div>
                  <div>
                    {isDoneStatus ? (
                      // DONE STATUS: Different message for completed chatbots
                      <>
                        <h3 className="text-xl font-semibold text-white mb-2">
                          📋 Deployment Instructions
                        </h3>
                        <p className="text-blue-300 text-sm">
                          Your chatbot is deployed and active. Here are your deployment codes and integration options.
                        </p>
                      </>
                    ) : (
                      // DEPLOY STATUS: Congratulations for immediate after payment
                      <>
                        <h3 className="text-xl font-semibold text-white mb-2">
                          🎉 Congratulations! Your chatbot is ready to deploy!
                        </h3>
                        <p className="text-green-300 text-sm">
                          Payment completed successfully. Your subscription is active with a 30-day free trial period.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Deployment Options */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Option 1: Add to Website */}
            <Card className="bg-gray-800/50 border-violet-500/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Globe className="w-6 h-6 mr-3 text-blue-400" />
                  1. Add to Website
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Add the code below to the header of your Website to display the chatbot on all pages.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Code Window */}
                <div className="relative">
                  <div className="bg-gray-900 rounded-lg p-4 border border-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Code className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-400 text-sm">HTML Code</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(chatbot.add_to_website_code, 'Add to Website')}
                        className="text-gray-400 hover:text-white"
                      >
                        {copiedCode === 'Add to Website' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <pre className="text-green-400 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                      {chatbot.add_to_website_code || ''}
                    </pre>
                  </div>
                </div>

                {/* Preview Image */}
                <div className="relative">
                  <img 
                    src="/addtoawebsite.png" 
                    alt="Add to Website Preview" 
                    className="w-full rounded-lg border border-gray-600"
                  />
                  <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Badge className="bg-blue-600/90 text-white">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Click to enlarge
                    </Badge>
                  </div>
                </div>

                <div className="text-sm text-gray-400 p-3 bg-blue-900/20 rounded-lg border border-blue-500/20">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  This will add a floating chat widget to all pages of your website. Visitors can click to start chatting.
                </div>
              </CardContent>
            </Card>

            {/* Option 2: Display Inside Webpage */}
            <Card className="bg-gray-800/50 border-violet-500/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Monitor className="w-6 h-6 mr-3 text-purple-400" />
                  2. Display Inside Webpage
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Display the open chatbot window inside a webpage with an iframe, ready to use.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Code Window */}
                <div className="relative">
                  <div className="bg-gray-900 rounded-lg p-4 border border-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Code className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-400 text-sm">iFrame Code</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(chatbot.display_inside_webpage_code, 'Display Inside Webpage')}
                        className="text-gray-400 hover:text-white"
                      >
                        {copiedCode === 'Display Inside Webpage' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <pre className="text-purple-400 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                      {chatbot.display_inside_webpage_code || ''}
                    </pre>
                  </div>
                </div>

                {/* Preview Image */}
                <div className="relative">
                  <img 
                    src="/displayinsidewebpage.png" 
                    alt="Display Inside Webpage Preview" 
                    className="w-full rounded-lg border border-gray-600"
                  />
                  <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Badge className="bg-purple-600/90 text-white">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Click to enlarge
                    </Badge>
                  </div>
                </div>

                <div className="text-sm text-gray-400 p-3 bg-purple-900/20 rounded-lg border border-purple-500/20">
                  <Monitor className="w-4 h-4 inline mr-1" />
                  This embeds the chat interface directly into your webpage. Perfect for dedicated support pages.
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Professional Plan WhatsApp Integration Notice - ONLY for Professional plans */}
          {isProPlan && (
            <div className="mt-8">
              <Card className="bg-green-900/20 border-green-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
                        <Sparkles className="w-4 h-4 mr-2" />
                        WhatsApp Integration Available
                      </h3>
                      <p className="text-green-300 text-sm mb-4">
                        As a Professional plan subscriber, you have access to WhatsApp integration for your chatbot.
                      </p>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2 bg-green-600/20 px-3 py-2 rounded-lg">
                          <Phone className="w-5 h-5 text-green-400" />
                          <span className="text-green-300 text-sm font-medium">WhatsApp Support</span>
                        </div>
                        <div className="text-gray-400 text-sm">
                          Contact: support@brixbot.ai or built-in chat
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Enterprise Plan Full Integration Notice - ONLY for Enterprise plans */}
          {isEnterprisePlanSelected && (
            <div className="mt-8">
              <Card className="bg-purple-900/20 border-purple-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageCircleMore className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Premium Integrations Available
                      </h3>
                      <p className="text-purple-300 text-sm mb-4">
                        As an Enterprise plan subscriber, you have access to our full suite of integrations including WhatsApp, Instagram, and Messenger.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <div className="flex items-center space-x-2 bg-green-600/20 px-3 py-2 rounded-lg">
                          <Phone className="w-5 h-5 text-green-400" />
                          <span className="text-green-300 text-sm font-medium">WhatsApp</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-pink-600/20 px-3 py-2 rounded-lg">
                          <Instagram className="w-5 h-5 text-pink-400" />
                          <span className="text-pink-300 text-sm font-medium">Instagram</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-blue-600/20 px-3 py-2 rounded-lg">
                          <MessageSquare className="w-5 h-5 text-blue-400" />
                          <span className="text-blue-300 text-sm font-medium">Messenger</span>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="text-gray-400 text-sm">
                          <Mail className="w-4 h-4 inline mr-1" />
                          Contact: support@brixbot.ai or built-in chat
                        </div>
                        <Button 
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          onClick={() => {
                            // Open built-in chat or email
                            window.open('mailto:support@brixbot.ai?subject=Enterprise Integration Setup Request&body=Hello,%0D%0A%0D%0AI would like to set up integrations for my Enterprise plan chatbot.%0D%0A%0D%0AChatbot: ' + encodeURIComponent(chatbot.company_name) + '%0D%0A%0D%0APlease contact me to discuss the setup process.%0D%0A%0D%0AThank you!', '_blank');
                          }}
                        >
                          Request Integration Setup
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Actions - Only show for deploy status, not for done status */}
          {!isDoneStatus && (
            <div className="mt-8 pt-6 border-t border-gray-700 flex justify-between">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={loading}
              >
                I'll Deploy Later
              </Button>
              <Button
                onClick={handleMarkComplete}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Marking Complete...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Deployed
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DeploymentModal;